import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes } from "react";
import { cn } from "@/lib/utils";
import { AlertCircle, CheckCircle2, Info } from "lucide-react";

const noteVariants = cva(
    "mt-6 flex gap-3 rounded-lg border p-4 text-sm leading-relaxed",
    {
        variants: {
            variant: {
                default:
                    "border-border bg-muted/60 text-foreground [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2",
                warning:
                    "border-amber-500/40 bg-amber-500/[0.1] text-foreground dark:border-amber-400/35 dark:bg-amber-400/[0.12] [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2",
                info:
                    "border-sky-600/35 bg-sky-600/[0.08] text-foreground dark:border-sky-400/35 dark:bg-sky-400/[0.12] [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2",
                success:
                    "border-emerald-600/35 bg-emerald-600/[0.08] text-foreground dark:border-emerald-400/35 dark:bg-emerald-400/[0.12] [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    },
);

const iconVariants = cva("h-5 w-5 shrink-0", {
    variants: {
        variant: {
            default: "text-foreground/65",
            warning: "text-amber-800 dark:text-amber-200",
            info: "text-sky-800 dark:text-sky-200",
            success: "text-emerald-800 dark:text-emerald-200",
        },
    },
    defaultVariants: {
        variant: "default",
    },
});

const icons = {
    default: AlertCircle,
    warning: AlertCircle,
    info: Info,
    success: CheckCircle2,
} as const;

export interface NoteProps
    extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof noteVariants> { }

export function Note({
    children,
    variant = "default",
    className,
    ...props
}: NoteProps) {
    const Icon = icons[variant ?? "default"];

    return (
        <div className={cn(noteVariants({ variant }), className)} {...props}>
            <Icon className={iconVariants({ variant })} aria-hidden />
            <div className="min-w-0 flex-1 [&>:first-child]:mt-0 [&>:last-child]:mb-0">
                {children}
            </div>
        </div>
    );
}
